/**
 * ErrorBoundary — fängt React-Render-Crashes ab und zeigt eine Fehlermeldung
 * statt White Screen of Death. Hilft beim Debug, weil der User die Fehler-
 * Meldung sieht und mitteilen kann statt nur "weißer Bildschirm".
 */
import { Component, ReactNode, ErrorInfo } from 'react';
import { Button } from '@/components/ui/button';
import { AlertCircle, RefreshCw } from 'lucide-react';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null, errorInfo: null };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ErrorBoundary:', error, errorInfo);
    this.setState({ errorInfo });
  }

  reset = () => {
    this.setState({ error: null, errorInfo: null });
  };

  render() {
    if (this.state.error) {
      if (this.props.fallback) return this.props.fallback;
      return (
        <div className="m-4 p-4 rounded-xl border-2 border-red-300 bg-red-50">
          <div className="flex items-start gap-3 mb-3">
            <AlertCircle className="w-6 h-6 text-red-700 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h2 className="text-lg font-semibold text-red-900">Etwas ist schiefgelaufen</h2>
              <p className="text-sm text-red-800 mt-1">
                Im UI ist ein Fehler aufgetreten — die Anwendung wurde davor bewahrt komplett abzustürzen.
              </p>
            </div>
          </div>
          <details className="text-xs bg-white/70 rounded p-2 mb-3 max-h-60 overflow-auto">
            <summary className="font-medium cursor-pointer">Fehler-Details</summary>
            <div className="mt-2 font-mono">
              <div className="font-semibold text-red-900">{this.state.error.name}:</div>
              <div className="text-red-800">{this.state.error.message}</div>
              {this.state.error.stack && (
                <pre className="mt-2 text-[10px] whitespace-pre-wrap break-all text-red-900/70">
                  {this.state.error.stack.split('\n').slice(0, 8).join('\n')}
                </pre>
              )}
              {this.state.errorInfo?.componentStack && (
                <pre className="mt-2 text-[10px] whitespace-pre-wrap break-all text-red-900/60">
                  Component Stack:
                  {this.state.errorInfo.componentStack.split('\n').slice(0, 6).join('\n')}
                </pre>
              )}
            </div>
          </details>
          <div className="flex gap-2">
            <Button size="sm" onClick={this.reset} variant="outline">
              <RefreshCw className="w-3.5 h-3.5 mr-1.5" />
              Erneut versuchen
            </Button>
            <Button size="sm" variant="ghost" onClick={() => window.location.reload()}>
              Seite neu laden
            </Button>
            <Button size="sm" variant="ghost" onClick={() => window.history.back()}>
              Zurück
            </Button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
